import {
  Box,
  TableCell,
  TableFooter,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { ReactElement, ReactNode, useCallback, useMemo, useState } from "react";

import { palette } from "~frontend/theme";

import { FlexBox } from "../FlexBox";
import Loading from "../Loading";
import Table, { TableColumns } from "../Table";
import { AnalyticsDashboardSection } from "./AnalyticsDashboardSection";
import { DataContainer, EmptyDataContainer } from "./DataContainer";

type AnalyticsDataTableColumn<Row extends { id: string }> =
  TableColumns<Row>[number] & {
    getSortableValue?: (row: Row) => string | number;
    getBarchartValue?: (row: Row) => number;
    renderFooter?: () => ReactNode;
  };

type AnalyticsDataTableColumns<Row extends { id: string }> =
  AnalyticsDataTableColumn<Row>[];

export function AnalyticsDataTable<
  Row extends { id: string },
  DownloadableRow extends Record<string, string>,
>({
  title,
  loading,
  rows,
  columns,
  defaultSortColumnKey,
  defaultSortDirection = "asc",
  downloadableRows,
  downloadFileName,
}: {
  title: ReactNode;
  loading?: boolean;
  rows: Row[];
  columns: AnalyticsDataTableColumns<Row>;
  defaultSortColumnKey?: string;
  defaultSortDirection?: "asc" | "desc";
  downloadableRows?: DownloadableRow[];
  downloadFileName?: string;
}): ReactElement {
  const [sortColumnKey, setSortColumnKey] = useState(defaultSortColumnKey);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);

  const handleToggleSort = useCallback(
    (key: string) => {
      if (key === sortColumnKey) {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else {
        setSortColumnKey(key);
        setSortDirection("asc");
      }
    },
    [sortColumnKey, sortDirection, setSortDirection, setSortColumnKey]
  );

  const sortedRows = useMemo(
    () =>
      sortColumnKey
        ? _.orderBy(
            rows,
            row =>
              columns.find(c => c.key === sortColumnKey)!.getSortableValue!(
                row
              ),
            sortDirection
          )
        : rows,
    [sortColumnKey, sortDirection, rows, columns]
  );

  const decoratedColumns = useMemo(() => {
    const barChartConfigByColumnKey: {
      [columnKey: string]: { getValue: (row: Row) => number; max: number };
    } = {};

    for (const column of columns) {
      if (column.getBarchartValue) {
        barChartConfigByColumnKey[column.key] = {
          getValue: column.getBarchartValue,
          max: _.max(rows.map(column.getBarchartValue)) ?? 0,
        };
      }
    }

    return columns.map(
      ({ key, getSortableValue, title, renderValue, ...column }) => {
        const barChartConfig = barChartConfigByColumnKey[key];
        return {
          ...column,
          key,
          align: "left" as const,
          title: getSortableValue ? (
            <FlexBox alignItems="flex-end">
              <Box sx={{ textOverflow: "wrap" }}>{title}</Box>
              <TableSortLabel
                active={sortColumnKey === key}
                direction={sortColumnKey === key ? sortDirection : "asc"}
                onClick={() => handleToggleSort(key)}
              />
            </FlexBox>
          ) : (
            <Box sx={{ textOverflow: "wrap" }}>{title}</Box>
          ),
          renderValue: (row: Row) =>
            barChartConfig ? (
              <FlexBox alignItems="center" gap="5px">
                <ValueBar
                  value={barChartConfig.getValue(row)}
                  max={barChartConfig.max}
                />
                {renderValue(row)}
              </FlexBox>
            ) : (
              renderValue(row)
            ),
        };
      }
    );
  }, [columns, rows, sortColumnKey, sortDirection, handleToggleSort]);

  const tableFooterProps = columns.some(column => column.renderFooter)
    ? {
        footer: (
          <TableFooter
            sx={{
              left: 0,
              bottom: 0,
              zIndex: 2,
              position: "sticky",
            }}
          >
            <TableRow>
              {columns.map(column => (
                <TableCell
                  sx={{
                    backgroundColor: palette.white,
                    borderTop: "1px solid #D7D5E0",

                    "&:not(:first-of-type)": {
                      textAlign: "center",
                    },
                  }}
                >
                  {column.renderFooter?.()}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        ),
      }
    : ({ noFooter: true } as const);

  return (
    <AnalyticsDashboardSection
      title={title}
      downloadDisabled={loading}
      downloadableRows={downloadableRows}
      downloadFileName={downloadFileName}
    >
      {loading || rows.length ? (
        <DataContainer
          sx={{
            width: "100%",
            height: "318px",
          }}
        >
          {loading ? (
            <FlexBox
              sx={{ width: "100%", height: "100%" }}
              alignItems="center"
              justifyContent="space-around"
            >
              <Loading />
            </FlexBox>
          ) : (
            <Table
              rows={sortedRows}
              columns={decoratedColumns}
              stickyHeader
              containerSx={{
                maxHeight: "318px",
                paddingX: "20px",
              }}
              {...tableFooterProps}
            />
          )}
        </DataContainer>
      ) : (
        <EmptyDataContainer
          sx={{
            width: "100%",
            height: "318px",
          }}
        >
          <FlexBox
            sx={{ width: "100%", height: "100%", color: palette.darkGray }}
            alignItems="center"
            justifyContent="space-around"
          >
            <Typography variant="body2">No available data</Typography>
          </FlexBox>
        </EmptyDataContainer>
      )}
    </AnalyticsDashboardSection>
  );
}

function ValueBar({
  value,
  max,
}: {
  value: number;
  max: number;
}): ReactElement {
  const nonZeroMax = max || 1;

  return (
    <FlexBox>
      <Box
        sx={{
          height: "8px",
          width: `${3 + 100 * (value / nonZeroMax)}px`,
          backgroundColor: palette.blue,
        }}
      />
      <Box
        sx={{
          height: "8px",
          width: `${3 + 100 * ((nonZeroMax - value) / nonZeroMax)}px`,
          backgroundColor: palette.midGray,
        }}
      />
    </FlexBox>
  );
}
